/**
 * Typed fetchers for the Clever LMS Connect admin endpoints.
 *
 * All calls go through the local admin-gated proxy
 * (`/api/proxy/clever-lms/*`) which forwards to the ai-tutor backend
 * (ENGG-1766).  Callers receive rich `CleverLmsError`s when the upstream
 * returns 404/503 so the UI can distinguish "endpoints not deployed"
 * and "feature not configured" from generic failures.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CleverDistrictLmsState =
  | "success"
  | "matching_in_progress"
  | "disconnected"
  | "error";

export interface CleverDistrict {
  clever_district_id: string;
  name: string;
  lms_state: CleverDistrictLmsState | string;
  lms_matched_percentage: number | null;
  last_lms_sync: string | null;
  student_count?: number | null;
  teacher_count?: number | null;
  section_count?: number | null;
}

export type UnresolvedRosterReason =
  | "no_evertutor_match"
  | "duplicate"
  | "incomplete"
  | string;

export interface UnresolvedRosterEntry {
  clever_id: string;
  entity_type: "user" | "section" | string;
  reason: UnresolvedRosterReason;
  details?: string | null;
  last_seen_at: string | null;
  clever_district_id?: string | null;
}

export interface AssignmentFailure {
  local_id: number | string;
  lesson_id?: number | string | null;
  lesson_name: string | null;
  classroom_id?: number | string | null;
  classroom_name: string | null;
  publish_state: string;
  last_sync_error: string | null;
  last_published_at: string | null;
  clever_district_id?: string | null;
}

export interface SubmissionFailure {
  submission_id?: number | string | null;
  clever_user_id: string;
  student_name?: string | null;
  assignment_id?: number | string | null;
  assignment_name: string | null;
  last_payload: unknown;
  last_sync_error: string | null;
  last_writeback_at: string | null;
}

export interface TriggerSyncResponse {
  success: boolean;
  run_id?: string;
  status?: string;
  detail?: string;
}

export interface RedriveAssignmentResponse {
  success: boolean;
  local_id: number | string;
  status?: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Error handling
// ---------------------------------------------------------------------------

/**
 * Error thrown for any non-2xx response from a Clever LMS endpoint.
 * `status` lets callers branch on 404 (endpoints not deployed) and 503
 * (backend not configured) to render the empty-state variants documented
 * in ENGG-1886.
 */
export class CleverLmsError extends Error {
  public readonly status: number;
  public readonly detail: string | null;

  constructor(status: number, detail: string | null, fallback: string) {
    super(detail || fallback);
    this.name = "CleverLmsError";
    this.status = status;
    this.detail = detail;
  }
}

export const isCleverLmsError = (value: unknown): value is CleverLmsError =>
  value instanceof CleverLmsError;

const CLEVER_LMS_BASE = "/api/proxy/clever-lms";

const defaultFetchOptions: RequestInit = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

async function readErrorDetail(response: Response): Promise<string | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    const parsed = JSON.parse(text) as {
      detail?: string;
      message?: string;
    };
    return parsed?.detail || parsed?.message || null;
  } catch {
    return null;
  }
}

async function requestCleverLms<T>(
  path: string,
  init: RequestInit = {},
  fallback = "Clever LMS request failed",
): Promise<T> {
  const response = await fetch(`${CLEVER_LMS_BASE}${path}`, {
    ...defaultFetchOptions,
    ...init,
    headers: {
      ...defaultFetchOptions.headers,
      ...(init.headers || {}),
    },
  });

  if (!response.ok) {
    const detail = await readErrorDetail(response);
    throw new CleverLmsError(response.status, detail, fallback);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

// ---------------------------------------------------------------------------
// Response normalizers
// ---------------------------------------------------------------------------

/**
 * Accept either a bare array response or the `{ items: [...] }` envelope.
 * Keeps the UI resilient to minor shape differences in the backend contract
 * while ENGG-1766 stabilises.
 */
function normalizeListResponse<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.items)) {
      return record.items as T[];
    }
    if (Array.isArray(record.districts)) {
      return record.districts as T[];
    }
    if (Array.isArray(record.failures)) {
      return record.failures as T[];
    }
    if (Array.isArray(record.entries)) {
      return record.entries as T[];
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const cleverLmsService = {
  async listDistricts(): Promise<CleverDistrict[]> {
    const payload = await requestCleverLms<unknown>(
      "/districts",
      undefined,
      "Failed to load Clever districts",
    );
    return normalizeListResponse<CleverDistrict>(payload);
  },

  async refreshDistrict(cleverDistrictId: string): Promise<CleverDistrict> {
    const path = `/districts/${encodeURIComponent(cleverDistrictId)}?refresh=true`;
    const payload = await requestCleverLms<unknown>(
      path,
      undefined,
      "Failed to refresh Clever district",
    );
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const record = payload as Record<string, unknown>;
      if (record.district && typeof record.district === "object") {
        return record.district as CleverDistrict;
      }
    }
    return payload as CleverDistrict;
  },

  async listUnresolvedRoster(): Promise<UnresolvedRosterEntry[]> {
    const payload = await requestCleverLms<unknown>(
      "/roster/unresolved",
      undefined,
      "Failed to load roster issues",
    );
    return normalizeListResponse<UnresolvedRosterEntry>(payload);
  },

  async listAssignmentFailures(): Promise<AssignmentFailure[]> {
    const payload = await requestCleverLms<unknown>(
      "/assignments/failures",
      undefined,
      "Failed to load publish failures",
    );
    return normalizeListResponse<AssignmentFailure>(payload);
  },

  async listSubmissionFailures(): Promise<SubmissionFailure[]> {
    const payload = await requestCleverLms<unknown>(
      "/submissions/failures",
      undefined,
      "Failed to load gradeback failures",
    );
    return normalizeListResponse<SubmissionFailure>(payload);
  },

  async triggerRosterSync(
    cleverDistrictId: string,
  ): Promise<TriggerSyncResponse> {
    const path = `/roster/${encodeURIComponent(cleverDistrictId)}/sync`;
    return requestCleverLms<TriggerSyncResponse>(
      path,
      { method: "POST" },
      "Failed to trigger roster sync",
    );
  },

  async redriveAssignment(localId: number | string): Promise<RedriveAssignmentResponse> {
    const path = `/assignments/${encodeURIComponent(String(localId))}/redrive`;
    return requestCleverLms<RedriveAssignmentResponse>(
      path,
      { method: "POST" },
      "Failed to re-drive assignment",
    );
  },
};

// Re-export the normalizer so tests can exercise it directly without going
// through the fetch pipeline.
export const __internals = { normalizeListResponse };
